// Sentry must be initialized before any other imports that might throw
import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import '~/styles/globals.css';
import '~/i18n.config';

import { App } from '~/app/app';
import { ENV } from '~/constants/config/env';
import type { TenantTheme } from '~/constants/config/tenant';
import { AppBootstrap } from '~/hoc/wrapper/app-bootstrap';
import { useAuthStore } from '~/store/auth.store';
import { attemptChunkRecovery, isChunkLoadError } from '~/utils/chunk-error';
import { applyTenantTheme } from '~/utils/tenant-theme';

// ---------------------------------------------------------------------------
// 1. Initialize Sentry
// ---------------------------------------------------------------------------
if (ENV.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: ENV.VITE_SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.MODE,
  });
}

/**
 * Unregister any service worker (and wipe its caches) left behind by an earlier
 * PWA-enabled deploy. Those lingering workers intercept navigations and serve
 * stale precache content, which is why a new build could keep showing old assets
 * even after a hard reload. PWA is disabled (vite.config.ts `disable: true`) so
 * nothing re-registers one. PROD-only so it never touches MSW's dev/e2e worker;
 * run once ever via a localStorage flag to avoid redundant work on every load.
 */
function cleanupLegacyServiceWorkers(): void {
  if (!import.meta.env.PROD) return;
  if (!('serviceWorker' in navigator)) return;
  try {
    if (localStorage.getItem('sw_cleanup_v1')) return;
  } catch {
    return; // storage unavailable — skip rather than risk throwing pre-mount
  }
  void Promise.all([
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => Promise.all(registrations.map((r) => r.unregister()))),
    caches.keys().then((names) => Promise.all(names.map((name) => caches.delete(name)))),
  ]).then(() => {
    try {
      localStorage.setItem('sw_cleanup_v1', '1');
    } catch {
      /* storage unavailable — cleanup still ran, it just re-runs next load */
    }
  });
}

/**
 * Recover automatically when a lazy page chunk can no longer be fetched — the signature of a
 * tab running an `index.html` from a previous deploy, whose hashed chunks Firebase Hosting no
 * longer has (it answers with index.html, so the browser rejects the wrong MIME type).
 *
 * Vite dispatches `vite:preloadError` on `window` for every failed dynamic import and rethrows
 * unless the event is cancelled. We deliberately leave it uncancelled: cancelling makes the
 * import resolve to `undefined`, and letting it throw means the router's error boundary can
 * still show a fallback if the guarded reload declines to fire.
 *
 * The `isChunkLoadError` gate is load-bearing, not defensive: Vite funnels module *evaluation*
 * errors through this same event (`baseModule().catch(handlePreloadError)`), and reloading on a
 * genuine crash would loop.
 *
 * PROD-only: in dev, a module that fails to transform makes the dev server return 500, which the
 * browser reports with the very same message — reloading there would hide the real error.
 */
function registerChunkErrorRecovery(): void {
  if (!import.meta.env.PROD) return;

  window.addEventListener('vite:preloadError', (event) => {
    if (!isChunkLoadError(event.payload)) return;
    void attemptChunkRecovery();
  });
}

// ---------------------------------------------------------------------------
// 2. Bootstrap application
// ---------------------------------------------------------------------------
async function bootstrap(): Promise<void> {
  // 2a-00. Listen for failed chunk fetches before anything lazy-loads, so a stale-deploy
  // failure during the first navigation is caught too.
  registerChunkErrorRecovery();

  // 2a-0. Remove any stale PWA service worker from a previous deploy so it can't
  // serve cached assets that survive a hard reload. Fire-and-forget.
  cleanupLegacyServiceWorkers();

  // 2a. Start MSW only when explicitly enabled (e.g. VITE_MSW=true)
  // Must run before React mounts — MSW intercepts fetch at the Service Worker
  // level and must be registered before any API calls fire.
  if (import.meta.env.DEV && import.meta.env.VITE_MSW === 'true') {
    try {
      const { worker } = await import('~/test/mocks/browser');
      await worker.start({ onUnhandledRequest: 'warn' });
      // Seed mock tokens so auth hydration has a token to work with
      useAuthStore.getState().setTokens('mock-access-token', 'mock-refresh-token');
    } catch {
      // MSW mocks not set up yet — that is fine, continue without them
    }
  }

  // 2b. Apply cached tenant theme from sessionStorage to prevent FOUC.
  // Skipped on the public /connector/* and /public/* pages: an external Ads owner opening a
  // share link in a tab that once held a platform session must not see that
  // tenant's colours.
  try {
    const { pathname } = window.location;
    const isPublicConnectorPage =
      pathname.startsWith('/connector/') || pathname.startsWith('/public/');
    const cached = isPublicConnectorPage ? null : sessionStorage.getItem('tenant-theme');
    if (cached) {
      const theme = JSON.parse(cached) as TenantTheme;
      applyTenantTheme(theme);
    }
  } catch {
    // Invalid or missing cached theme — ignore
  }

  // 2c. Mount React — AppBootstrap handles auth hydration, tenant prefetch,
  // and i18n init inside the React tree so the browser paints a skeleton
  // instead of a blank screen while those async operations complete.
  const rootElement = document.getElementById('root');
  if (!rootElement) throw new Error('Root element not found');

  createRoot(rootElement).render(
    <StrictMode>
      <AppBootstrap>
        <App />
      </AppBootstrap>
    </StrictMode>,
  );
}

void bootstrap();
